import React from 'react';
import { Query } from '@apollo/client/react/components';
import Styled from 'styled';
import { mapAfter } from 'apollo-relay/update-query';
import { Route } from 'react-router-dom';

import observe from 'lib/intersection-observer/observe';
import LoadingProgress from 'lib/react-router/loading-progress';

import ErrorPage from 'four-nets/errors/error-page';
import Unauthorized from 'four-nets/errors/401/unauthorized';
import { ViewerConsumer } from 'four-nets/user/viewer/viewer';

import FetchMoreButton from 'four-nets/ui-v2/fetch-more-button/fetch-more-button';
import NiceImageGrid from 'four-nets/photoblog/posts/album/detail/nice-image-grid/nice-image-grid';

import query, { GroupPhotosLoaderQuery, GroupPhotosLoaderQueryVariables } from './group-photos-loader.graphql';
import defaultStyles from './group-photos.scss';
import { fromGlobalId } from 'graphql-relay';

export interface Props {
    styles: typeof defaultStyles;
    id: string;
}

interface State {
    loading: boolean;
    loadedMore: boolean;
}

const FIRST = 20;

class GroupPhotos extends React.PureComponent<Props, State> {
    state = { loading: false, loadedMore: false };
    unobserve?: () => void;
    buttonRef = React.createRef<HTMLButtonElement>();
    fetchMore?: any;
    data?: any;

    componentWillUnmount() {
        if (this.unobserve) {
            this.unobserve();
        }
    }

    fetchMoreWrapper = () => {
        this.setState({ loading: true });

        const data = this.data!;
        const fetchMore = this.fetchMore!;

        const endCursor = data!.group!.images!.pageInfo.endCursor;

        fetchMore({
            variables: { after: endCursor, first: FIRST },
            updateQuery: (
                previousResult: GroupPhotosLoaderQuery = {} as GroupPhotosLoaderQuery,
                { fetchMoreResult }: { fetchMoreResult: any }
            ) => {
                if (!fetchMoreResult) {
                    return previousResult;
                }
                return {
                    ...previousResult,
                    ...fetchMoreResult,
                    group: {
                        ...previousResult.group,
                        ...fetchMoreResult.group,
                        images: mapAfter(previousResult!.group!.images, fetchMoreResult!.group!.images),
                    },
                } as GroupPhotosLoaderQuery;
            },
        }).finally(() =>
            this.setState({ loading: false, loadedMore: true }, () => {
                if (!this.unobserve && this.buttonRef && this.buttonRef.current) {
                    this.unobserve = observe(this.buttonRef.current as Element, () => {
                        if (!this.state.loading) {
                            this.fetchMoreWrapper();
                        }
                    });
                }
            })
        );
    };

    render() {
        const { id, styles } = this.props;
        return (
            <ViewerConsumer>
                {({ viewer }) =>
                    !viewer ? (
                        <Unauthorized />
                    ) : (
                        <Query<GroupPhotosLoaderQuery, GroupPhotosLoaderQueryVariables>
                            query={query}
                            variables={{ id, first: FIRST }}
                            notifyOnNetworkStatusChange={true}
                            fetchPolicy='cache-first'
                        >
                            {({ error, data, loading, fetchMore }) => {
                                this.fetchMore = fetchMore;
                                this.data = data;

                                return (
                                    <LoadingProgress loading={loading}>
                                        <ErrorPage error={error}>
                                            {data && data.group ? (
                                                <div className={styles.groupPhotos}>
                                                    <Route>
                                                        {({ history }) => (
                                                            <NiceImageGrid
                                                                images={data!.group!.images}
                                                                wrapWidth={620}
                                                                onClick={(_: any, props: any) => {
                                                                    const postSlug =
                                                                        props && props.post && props.post.slug;
                                                                    const groupId = fromGlobalId(
                                                                        (data.group && data.group.id) || ''
                                                                    ).id;
                                                                    history.push(`/group/${groupId}/${postSlug}/`);
                                                                }}
                                                            />
                                                        )}
                                                    </Route>
                                                    {data.group.images && data.group.images.pageInfo.hasNextPage ? (
                                                        <FetchMoreButton
                                                            ref={this.buttonRef}
                                                            disabled={this.state.loading}
                                                            loading={this.state.loading}
                                                            onClick={this.fetchMoreWrapper}
                                                        />
                                                    ) : null}
                                                </div>
                                            ) : this.state.loading ? null : null}{' '}
                                            {/* Maybe add placeholder 'loading' images ? */}
                                        </ErrorPage>
                                    </LoadingProgress>
                                );
                            }}
                        </Query>
                    )
                }
            </ViewerConsumer>
        );
    }
}

export default Styled(defaultStyles)(GroupPhotos);
